// frontend/src/components/FolderTree.jsx
import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation } from "react-router-dom";
import { getFolderTree } from "../api/folders";

/**
 * Read the active folder id from a location's query string.
 * @param {string} search  location.search (e.g. "?folder=abc")
 * @returns {string|null}
 */
function activeFolderId(search) {
  return new URLSearchParams(search).get("folder");
}

/**
 * FolderNode — one row in the folder tree, with a collapse caret for branches
 * that have children. Clicking the row navigates to /codes?folder=<id>.
 */
function FolderNode({ node, depth, selectedId, onSelect }) {
  const [open, setOpen] = useState(true);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedId === node.id;

  return (
    <li>
      <div
        className={`group flex items-center gap-1 rounded-md pr-2 text-sm transition-colors ${
          isSelected
            ? "bg-blue-50 text-blue-700"
            : "text-gray-600 hover:bg-gray-100"
        }`}
        style={{ paddingLeft: `${0.25 + depth * 0.75}rem` }}
      >
        {hasChildren ? (
          <button
            type="button"
            aria-label={open ? "Collapse" : "Expand"}
            onClick={() => setOpen((o) => !o)}
            className="p-0.5 text-gray-400 hover:text-gray-700 shrink-0"
          >
            <svg
              className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <span className="w-4 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => onSelect(node.id)}
          className="flex-1 min-w-0 flex items-center justify-between gap-2 py-1.5 text-left"
        >
          <span className="truncate">{node.name}</span>
          <span
            className={`shrink-0 text-xs tabular-nums ${
              isSelected ? "text-blue-500" : "text-gray-400"
            }`}
          >
            {node.code_count}
          </span>
        </button>
      </div>
      {hasChildren && open && (
        <ul>
          {node.children.map((child) => (
            <FolderNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

FolderNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    code_count: PropTypes.number.isRequired,
    children: PropTypes.array,
  }).isRequired,
  depth: PropTypes.number.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};
FolderNode.defaultProps = { selectedId: null };

/**
 * FolderTree — collapsible folder navigator for the sidebar (P14-G8).
 *
 * Fetches the org's folder tree and lists it with "All codes" at the top.
 * Selecting a folder navigates to /codes?folder=<id> so the CodesPage (which
 * reads the param) filters its list; "All codes" clears the filter. Fetch
 * failures are swallowed so the rest of the sidebar always renders.
 *
 * An optional `refreshKey` prop lets parents force a re-fetch (e.g. after a
 * folder is created elsewhere).
 */
function FolderTree({ refreshKey }) {
  const [tree, setTree] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedId = activeFolderId(location.search);
  const onCodes = location.pathname === "/codes";

  const [bump, setBump] = useState(0);

  // Refetch when another part of the app changes folders/counts (folder
  // created, code moved, bulk action) by dispatching `qrflow:folders-changed`.
  useEffect(() => {
    const handler = () => setBump((b) => b + 1);
    window.addEventListener("qrflow:folders-changed", handler);
    return () => window.removeEventListener("qrflow:folders-changed", handler);
  }, []);

  useEffect(() => {
    let cancelled = false;
    getFolderTree()
      .then((data) => {
        if (!cancelled) setTree(data);
      })
      .catch(() => {
        if (!cancelled) setTree([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, bump]);

  const select = useCallback(
    (folderId) => {
      navigate(folderId ? `/codes?folder=${folderId}` : "/codes");
    },
    [navigate],
  );

  return (
    <div className="px-3 py-3 border-t border-gray-200">
      <p className="px-1 mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        Folders
      </p>
      <ul>
        <li>
          <button
            type="button"
            onClick={() => select(null)}
            className={`w-full flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
              onCodes && !selectedId
                ? "bg-blue-50 text-blue-700"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            <span>All codes</span>
          </button>
        </li>
        {tree.map((node) => (
          <FolderNode
            key={node.id}
            node={node}
            depth={0}
            selectedId={onCodes ? selectedId : null}
            onSelect={select}
          />
        ))}
        {tree.length === 0 && (
          <li className="px-2 py-1.5 text-xs text-gray-400 italic">
            No folders yet
          </li>
        )}
      </ul>
    </div>
  );
}

FolderTree.propTypes = {
  refreshKey: PropTypes.number,
};
FolderTree.defaultProps = { refreshKey: 0 };

export default FolderTree;
